@layer refine.base {
  :host {
    --_bg: var(--button-bg, var(--color-contrast-100));
    --_color: var(--button-color, var(--color-contrast-900));
    --_border-color: var(--button-border-color, var(--color-contrast-300));
    --_radius: var(--button-radius, var(--rounded-lg));

    display: inline-flex;
    align-items: center;
    justify-content: var(--button-justify, center);
  }

  /* Link mode: disable pointer interaction on the inner <a> while disabled/loading. */
  :host([disabled]) a,
  :host([loading]) a {
    pointer-events: none;
    cursor: not-allowed;
  }

  :host([loading]) a {
    cursor: wait;
  }

  [part='button'] {
    position: relative;
    box-sizing: border-box;
    display: flex;
    gap: var(--_gap, var(--size-2));
    align-items: center;
    justify-content: var(--button-justify, center);
    width: 100%;
    min-width: 0;
    height: var(--_height, var(--size-10));
    min-height: var(--_touch-target);
    padding: var(--_padding, var(--size-2) var(--size-4));
    font-size: var(--_font-size, var(--text-sm));
    font-weight: var(--font-medium);
    line-height: var(--_line-height, var(--leading-normal));
    color: var(--_color);
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;
    user-select: none;
    background: var(--_bg);
    border: var(--button-border, var(--border)) solid var(--_border-color);
    border-block-start-width: var(--button-border-top, var(--button-border, var(--border)));
    border-block-end-width: var(--button-border-bottom, var(--button-border, var(--border)));
    border-inline-start-width: var(--button-border-start, var(--button-border, var(--border)));
    border-inline-end-width: var(--button-border-end, var(--button-border, var(--border)));
    border-radius: var(--_radius);
    transition: var(
      --_motion-transition,
      background var(--transition-normal),
      border-color var(--transition-normal),
      box-shadow var(--transition-normal),
      color var(--transition-normal),
      transform 120ms var(--ease-spring)
    );
  }

  [part='button']:focus-visible {
    outline: none;
  }

  /* Keyframe must live in shadow DOM — @keyframes don't inherit across shadow roots */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .loader {
    position: absolute;
    width: 1em;
    height: 1em;
    border: var(--border-2) solid currentcolor;
    border-inline-end-color: transparent;
    border-radius: 50%;
    animation: var(--_motion-animation, spin var(--transition-loader) infinite);
  }

  .content {
    display: inline-flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    white-space: nowrap;
  }

  ::slotted([slot='prefix']),
  ::slotted([slot='suffix']) {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
  }

  ::slotted(svg) {
    flex-shrink: 0;
    width: var(--_icon-size, var(--leading-6));
    height: var(--_icon-size, var(--leading-6));
  }
}

@layer refine.overrides {
  :host([fullwidth]) {
    display: flex;
    width: 100%;
  }

  /* `[part='button']`'s own height otherwise comes from the fixed size-preset var
     (`--_height`, default `--size-10`) — overriding just the host's own box height (e.g. via an
     external `height: 100%` from a swipe-action panel container) stretches an invisible wrapper
     without moving the visible button surface at all. Both declarations are needed. */
  :host([fullheight]) {
    display: flex;
    height: 100%;
  }

  :host([fullheight]) [part='button'] {
    height: 100%;
  }

  /* Icon-only always uses perfect circle */
  :host([rounded][icon-only]) [part='button'] {
    border-radius: 50%;
  }

  :host([loading]) .content {
    visibility: hidden;
  }
}

@layer refine.variants {
  :host([icon-only]) [part='button'] {
    aspect-ratio: 1;
    padding: 0;
  }

  /* ── Per-variant token assignments ─────────────────────────────────────────
     Each variant sets --_bg/--_color/--_border-color (consuming the public
     --button-* tokens as overrides) plus optional --_hover-* / --_active-*
     tokens for interactive states.
     The shared :is(button, a):hover / :active rules below read these tokens.
     ─────────────────────────────────────────────────────────────────────── */

  /* Solid (Default) */
  :host(:not([variant])),
  :host([variant='solid']) {
    --_bg: var(--button-bg, var(--_theme-base));
    --_color: var(--button-color, var(--_theme-contrast));
    --_border-color: var(--button-border-color, var(--_theme-base));
    --_hover-bg: var(--_theme-focus);
    --_active-bg: color-mix(in oklch, var(--_theme-focus) 85%, black);
    --_active-color: var(--_theme-contrast);
  }

  /* Flat */
  :host([variant='flat']) {
    --_bg: var(--button-bg, color-mix(in oklch, var(--_theme-backdrop) 25%, var(--color-contrast-100)));
    --_color: var(--button-color, var(--_theme-base));
    --_border-color: var(--button-border-color, color-mix(in oklch, var(--_theme-focus) 20%, transparent));
    --_hover-bg: var(--_theme-focus);
    --_hover-color: var(--_theme-contrast);
    --_active-bg: color-mix(in oklch, var(--_theme-focus) 85%, black);
    --_active-color: var(--_theme-contrast);
  }

  :host([variant='flat']) [part='button'] {
    box-shadow: var(--inset-shadow-2xs);
  }

  :host([variant='flat']) [part='button']:hover {
    box-shadow: var(--inset-shadow-xs);
  }

  :host([variant='flat']) [part='button']:active {
    box-shadow: var(--inset-shadow-sm);
  }

  /* Bordered */
  :host([variant='bordered']) {
    --_bg: var(--button-bg, var(--_theme-backdrop));
    --_color: var(--button-color, var(--_theme-base));
    --_border-color: var(--button-border-color, var(--_theme-border));
    --_hover-bg: var(--_theme-focus);
    --_hover-color: var(--_theme-contrast);
    --_hover-border-color: var(--_theme-focus);
    --_active-bg: color-mix(in oklch, var(--_theme-focus) 85%, black);
    --_active-color: var(--_theme-contrast);
  }

  :host([variant='bordered']) [part='button'] {
    box-shadow: var(--inset-shadow-xs);
  }

  :host([variant='bordered']) [part='button']:hover {
    box-shadow: var(--inset-shadow-xs);
  }

  :host([variant='bordered']) [part='button']:active {
    box-shadow: var(--inset-shadow-sm);
  }

  /* Outline */
  :host([variant='outline']) {
    --_bg: var(--button-bg, transparent);
    --_color: var(--button-color, var(--_theme-base));
    --_border-color: var(--button-border-color, var(--_theme-base));
    --_hover-bg: var(--_theme-backdrop);
    --_hover-border-color: var(--_theme-focus);
    --_active-bg: color-mix(in oklch, var(--_theme-base) 85%, black);
    --_active-color: var(--_theme-contrast);
  }

  /* Ghost */
  :host([variant='ghost']) {
    --_bg: var(--button-bg, transparent);
    --_color: var(--button-color, var(--_theme-base));
    --_border-color: var(--button-border-color, transparent);
    --_hover-bg: var(--_theme-backdrop);
    --_active-bg: color-mix(in oklch, var(--_theme-base) 85%, black);
    --_active-color: var(--_theme-contrast);
  }

  :host([variant='ghost']) [part='button'] {
    border-width: 0;
  }

  /* Text */
  :host([variant='text']) {
    --_bg: var(--button-bg, transparent);
    --_color: var(--button-color, var(--_theme-base));
    --_border-color: var(--button-border-color, transparent);
    --_hover-color: var(--_theme-focus);
    --_active-color: color-mix(in oklch, var(--_theme-focus) 85%, black);
  }

  :host([variant='text']) [part='button'] {
    border-width: 0;
  }

  /* Frost — sets its own interactive tokens so the shared rules below apply uniformly */
  :host([variant='frost']:not([color])) {
    --_hover-bg: var(--button-frost-active-bg, color-mix(in oklch, var(--color-canvas) 70%, transparent));
    --_hover-border-color: var(
      --button-frost-active-border-color,
      color-mix(in oklch, var(--_theme-focus) 40%, transparent)
    );
    --_active-bg: var(--button-frost-active-bg, color-mix(in oklch, var(--color-canvas) 70%, transparent));
    --_active-border-color: var(
      --button-frost-active-border-color,
      color-mix(in oklch, var(--_theme-focus) 40%, transparent)
    );
  }

  :host([variant='frost'][color]) {
    --_hover-bg: var(--button-frost-active-bg, color-mix(in oklch, var(--_theme-backdrop) 50%, var(--_theme-base) 50%));
    --_hover-border-color: var(
      --button-frost-active-border-color,
      color-mix(in oklch, var(--_theme-focus) 65%, transparent)
    );
    --_active-bg: var(
      --button-frost-active-bg,
      color-mix(in oklch, var(--_theme-backdrop) 50%, var(--_theme-base) 50%)
    );
    --_active-border-color: var(
      --button-frost-active-border-color,
      color-mix(in oklch, var(--_theme-focus) 65%, transparent)
    );
  }

  /* ── Shared interactive states — all variants use --_* tokens set above ── */

  [part='button']:hover {
    color: var(--_hover-color, var(--_color));
    background: var(--_hover-bg, var(--_bg));
    border-color: var(--_hover-border-color, var(--_border-color));
  }

  [part='button']:active {
    color: var(--_active-color, var(--_color));
    background: var(--_active-bg, var(--_bg));
    border-color: var(--_active-border-color, var(--_border-color));
    box-shadow: var(--inset-shadow-2xs);
  }
}

@layer refine.overrides {
  /* Focus ring — keyboard navigation only; combines focus shadow + halo to avoid shadow pop */
  [part='button']:focus-visible {
    outline: none;
    box-shadow: var(--_theme-shadow), var(--_theme-halo);
  }

  /* Hover depth — halo border effect matching ore-input focus style */
  [part='button']:hover,
  [part='button']:active {
    border-color: var(--_hover-border-color, var(--_theme-focus));
    box-shadow: var(--_theme-shadow), var(--_theme-halo);
  }

  /* Ghost + Text variants: no border/shadow decoration on hover */
  :host([variant='ghost']) [part='button']:hover,
  :host([variant='text']) [part='button']:hover {
    border-color: transparent;
    box-shadow: none;
    transform: none;
  }

  /* ...nor on :active — stay borderless even though the shared rule above now
     adds a halo to :active too (physical press transform below still applies,
     this only cancels border-color/box-shadow). */
  :host([variant='ghost']) [part='button']:active,
  :host([variant='text']) [part='button']:active {
    border-color: transparent;
    box-shadow: none;
  }

  /* Physical press feedback */
  [part='button']:active {
    transform: translateY(1px);
  }
}
